import { CertificateIcon, PhoneIcon } from "@phosphor-icons/react";

import type { SafetyProfilePayload } from "@miter/types/contracts";

const CompanyMark = ({ name, logoUrl }: { name: string; logoUrl: string | null }): React.JSX.Element => {
  const base =
    "tw:flex tw:size-14 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-sm tw:border tw:border-stroke tw:p-2";
  if (logoUrl) {
    return (
      <div className={`${base} tw:bg-white`}>
        <img src={logoUrl} alt="" className="tw:size-full tw:object-contain" />
      </div>
    );
  }

  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  return (
    <div className={`${base} tw:bg-primary-lilac tw:text-lg tw:font-medium tw:text-primary-purple`}>
      {initials}
    </div>
  );
};

type IdentityFactProps = {
  icon: React.ReactNode;
  children: React.ReactNode;
};

const IdentityFact = ({ icon, children }: IdentityFactProps): React.JSX.Element => (
  <span className="tw:inline-flex tw:items-center tw:gap-1.5">
    <span className="tw:flex tw:shrink-0 tw:text-midnight/35">{icon}</span>
    {children}
  </span>
);

type SafetyProfileIdentityProps = {
  company: SafetyProfilePayload["company"];
};

export const SafetyProfileIdentity = ({ company }: SafetyProfileIdentityProps): React.JSX.Element => (
  <div className="tw:flex tw:items-center tw:gap-4 tw:bg-white tw:px-5 tw:pb-5 tw:pt-6">
    <CompanyMark name={company.name} logoUrl={company.logo_url} />
    <div className="tw:min-w-0 tw:flex-1">
      <h1 className="tw:m-0 tw:font-denim tw:text-2xl tw:text-midnight">{company.name}</h1>
      <div className="tw:mt-1 tw:flex tw:flex-wrap tw:gap-x-5 tw:gap-y-1 tw:text-sm tw:text-midnight/70">
        {company.address && <span>{company.address}</span>}
        {company.phone && (
          <IdentityFact icon={<PhoneIcon className="tw:size-4" />}>
            <a
              href={`tel:${company.phone}`}
              className="tw:text-midnight/70 tw:no-underline tw:hover:underline"
            >
              {company.phone}
            </a>
          </IdentityFact>
        )}
        {company.license && (
          <IdentityFact icon={<CertificateIcon className="tw:size-4" />}>{company.license}</IdentityFact>
        )}
      </div>
    </div>
  </div>
);
